// src/pages/ShopifySyncIssues.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { apiGet, apiPost } from "../../api/client";
import { A, pillClass } from "../../styles/appleClasses";
import { Button } from "../../components/Button";
import SectionShell, {
  type SectionTab,
} from "../../components/layout/SectionShell";
import UpgradeModal from "../../components/UpgradeModal";
import type { AuthMe } from "../../types/auth";
import { companyRoute } from "../../utils/routeHelpers";
import ShopifyBillingRequiredPanel from "../../components/shopify/ShopifyBillingRequiredPanel";
import {
  isShopifyBillingRequired,
  type ShopifyStatus,
} from "../../types/shopify";

type SyncError = {
  id: number;
  scope: string;
  shopify_object_type?: string | null;
  shopify_object_id?: string | null;
  issue_code?: string | null;
  message: string;
  detail_json?: unknown;
  status: string;
  created_at?: string | null;
  resolved_at?: string | null;
};

const SHOPIFY_TABS: SectionTab[] = [
  { label: "Overview", to: "/commerce/shopify", end: true },
  { label: "Product Mapping", to: "/commerce/shopify/product-mapping" },
  { label: "Sync Issues", to: "/commerce/shopify/sync-issues" },
  { label: "Activity", to: "/commerce/shopify/diagnostics" },
];

function shortDate(value?: string | null) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function titleize(value?: string | null) {
  if (!value) return "Unknown";
  return value
    .replace(/_/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function isProUser(me: AuthMe | null) {
  const tier = (me?.account_tier || "").toLowerCase();
  return (
    !!me?.is_user_pro ||
    tier === "pro" ||
    tier === "paid" ||
    tier === "enterprise"
  );
}

function normalizeCode(issue: SyncError) {
  if (issue.issue_code) return issue.issue_code;
  const detail = issue.detail_json as any;
  if (detail && typeof detail === "object") {
    return detail.issue_code || detail.code || detail.reason || null;
  }
  return null;
}

function issueType(issue: SyncError) {
  const status = (issue.status || "").toLowerCase();
  const code = (normalizeCode(issue) || "").toLowerCase();
  const msg = (issue.message || "").toLowerCase();

  if (status === "resolved") {
    return { label: "Resolved", tone: "success" as const };
  }

  if (
    code.includes("plan") ||
    code.includes("commerce_integration_gate") ||
    code.includes("connector") ||
    msg.includes("upgrade to pro")
  ) {
    return { label: "Plan", tone: "brand" as const };
  }

  if (
    code.includes("unmapped") ||
    code.includes("mapping") ||
    msg.includes("unmapped") ||
    msg.includes("not linked")
  ) {
    return { label: "Product mapping", tone: "warning" as const };
  }

  if (
    code.includes("insufficient") ||
    code.includes("stock") ||
    msg.includes("insufficient stock")
  ) {
    return { label: "Inventory", tone: "warning" as const };
  }

  if (code.includes("refund") || msg.includes("refund")) {
    return { label: "Refund", tone: "warning" as const };
  }

  if (code.includes("cancel") || msg.includes("cancel")) {
    return { label: "Cancellation", tone: "warning" as const };
  }

  if (code.includes("unsupported") || msg.includes("unsupported")) {
    return { label: "Unsupported", tone: "warning" as const };
  }

  if (code.includes("invalid") || msg.includes("invalid")) {
    return { label: "Data check", tone: "warning" as const };
  }

  return { label: "Review", tone: "warning" as const };
}

function issueTitle(issue: SyncError) {
  const type = issueType(issue).label;

  if (type === "Resolved") return "Issue resolved";
  if (type === "Plan") return "Shopify access required";
  if (type === "Product mapping") return "Product mapping required";
  if (type === "Inventory") return "Inventory review required";
  if (type === "Refund") return "Refund review required";
  if (type === "Cancellation") return "Cancellation review required";
  if (type === "Unsupported") return "Manual review required";
  if (type === "Data check") return "Shopify data needs review";

  return "Shopify item needs review";
}

function shortMessage(issue: SyncError) {
  const type = issueType(issue).label;

  if (type === "Resolved") return "No action is needed.";
  if (type === "Plan")
    return "Shopify processing is paused until access is active.";
  if (type === "Product mapping")
    return "This sale was not posted because a Shopify product is not mapped.";
  if (type === "Inventory")
    return "This sale was not posted because inventory needs review.";
  if (type === "Refund") return "This refund was not posted automatically.";
  if (type === "Cancellation")
    return "This cancellation was not posted automatically.";
  if (type === "Unsupported")
    return "Nimos did not auto-post this Shopify activity.";
  if (type === "Data check")
    return "Nimos could not safely read this Shopify data.";

  return "Nimos did not auto-post this Shopify activity.";
}

function issueContextLine(issue: SyncError) {
  const detail = issue.detail_json as any;
  const unmapped = Array.isArray(detail?.unmapped) ? detail.unmapped : [];

  const productNames = unmapped
    .map((item: any) => item?.title)
    .filter(Boolean)
    .join(", ");

  if (productNames) return `Product: ${productNames}`;

  if (detail?.title) return `Product: ${detail.title}`;
  if (detail?.product_title) return `Product: ${detail.product_title}`;
  if (detail?.variant_title) return `Product: ${detail.variant_title}`;

  if (detail?.order_name) return `Order: ${detail.order_name}`;
  if (detail?.order_id) return `Order: ${detail.order_id}`;

  return shortMessage(issue);
}

function suggestedAction(issue: SyncError) {
  const type = issueType(issue).label;

  if (type === "Resolved") return "No action needed.";
  if (type === "Plan") return "Activate Shopify access, then refresh.";

  if (type === "Product mapping")
    return "Post the sale manually if needed, then mark as reviewed. Map the product for future syncs.";

  if (type === "Inventory")
    return "Check stock. Post the sale or adjustment manually, then mark as reviewed.";

  if (type === "Refund")
    return "Check it against the original sale. Record the refund or adjustment manually, then mark as reviewed.";

  if (type === "Cancellation")
    return "Record any stock, receivable or payment adjustment manually, then mark as reviewed.";

  if (type === "Unsupported")
    return "Record anything needed manually, then mark as reviewed.";

  if (type === "Data check")
    return "Review the details first. Confirm whether a manual entry is needed before marking reviewed.";

  return "Review the Shopify record first. Manually record anything needed in Nimos before marking reviewed.";
}

function isMappingIssue(issue: SyncError) {
  return issueType(issue).label === "Product mapping";
}

function issueDetails(issue: SyncError) {
  return JSON.stringify(
    {
      id: issue.id,
      scope: issue.scope,
      status: issue.status,
      issue_type: issueType(issue).label,
      object_type: issue.shopify_object_type,
      object_id: issue.shopify_object_id,
      issue_code: normalizeCode(issue),
      message: issue.message,
      created_at: issue.created_at,
      resolved_at: issue.resolved_at,
      detail_json: issue.detail_json,
    },
    null,
    2,
  );
}

function shopifyAdminOrderUrl(
  shopDomain?: string | null,
  shopifyOrderId?: string | null,
) {
  const shop = (shopDomain || "").trim().toLowerCase();
  const orderId = (shopifyOrderId || "").trim();

  if (!shop || !orderId) return null;

  return `https://${shop}/admin/orders/${encodeURIComponent(orderId)}`;
}

export default function ShopifySyncIssues() {
  const location = useLocation();
  const { company_id } = useParams<{ company_id: string }>();
  const [me, setMe] = useState<AuthMe | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [resolvingId, setResolvingId] = useState<number | null>(null);
  const [issues, setIssues] = useState<SyncError[]>([]);
  const [shopifyStatus, setShopifyStatus] = useState<ShopifyStatus | null>(null);
  const [shopDomain, setShopDomain] = useState<string | null>(null);

  const query = new URLSearchParams(location.search).get("q") || "";
  const isPro = isProUser(me);
  const shopifyBillingRequired = isShopifyBillingRequired(shopifyStatus);
  const shouldShowShopifyProUpsell =
    shopifyStatus !== null && !isPro && !shopifyBillingRequired;

  async function refresh() {
    setBusy(true);
    setErr(null);

    try {
      const statusRes = await apiGet<ShopifyStatus>("/shopify/status");
      setShopifyStatus(statusRes);
      setShopDomain(statusRes?.shop_domain || null);

      if (isShopifyBillingRequired(statusRes)) {
        setIssues([]);
        return;
      }

      const rows = await apiGet<SyncError[]>("/shopify/sync-errors");
      setIssues(rows || []);
    } catch (e: any) {
      setErr(
        e?.response?.data?.detail ||
          e?.message ||
          "Failed to load Shopify sync issues.",
      );
    } finally {
      setLoading(false);
      setBusy(false);
    }
  }

  useEffect(() => {
    let active = true;

    apiGet<AuthMe>("/auth/me")
      .then((res) => {
        if (active) setMe(res);
      })
      .catch(() => {
        if (active) setMe(null);
      });

    refresh();

    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return issues;
    return issues.filter((issue) =>
      [
        issue.id,
        issue.scope,
        issue.status,
        issue.shopify_object_type,
        issue.shopify_object_id,
        normalizeCode(issue),
        issue.message,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [issues, query]);

  const activeIssues = issues.filter(
    (issue) => (issue.status || "").toLowerCase() !== "resolved",
  );
  const resolvedIssues = issues.length - activeIssues.length;

  async function copyIssue(issue: SyncError) {
    const text = issueDetails(issue);
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(issue.id);
      window.setTimeout(() => setCopiedId(null), 1800);
    } catch {
      window.prompt("Copy issue details", text);
    }
  }

  async function markReviewed(issue: SyncError) {
    if ((issue.status || "").toLowerCase() === "resolved") return;

    const ok = window.confirm(
      "Mark as reviewed? Record anything needed in Nimos first.",
    );

    if (!ok) return;

    setResolvingId(issue.id);
    setErr(null);

    try {
      await apiPost(`/shopify/sync-errors/${issue.id}/resolve`, {});
      await refresh();
    } catch (e: any) {
      setErr(
        e?.response?.data?.detail ||
          e?.message ||
          "Failed to mark Shopify issue as reviewed.",
      );
    } finally {
      setResolvingId(null);
    }
  }

  return (
    <SectionShell
      title="Shopify"
      tabs={SHOPIFY_TABS}
      tabsAriaLabel="Shopify sections"
      closePath="/commerce/shopify"
    >
      <main className="mx-auto w-full max-w-3xl">
        {shopifyBillingRequired && shopifyStatus ? (
          <ShopifyBillingRequiredPanel
            installedAppUrl={shopifyStatus.installed_app_url}
            shopifyAdminAppUrl={shopifyStatus.shopify_admin_app_url}
          />
        ) : (
        <div className={`${A.card} p-4 sm:p-5`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className={A.title}>Sync issues</h2>
              <p className={`${A.label} mt-1`}>
                Activity Nimos couldn't post. Record what's needed, then mark as reviewed.
              </p>
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={refresh}
              disabled={busy}
              minWidth="auto"
              className="px-3"
            >
              {busy ? "Refreshing…" : "Refresh"}
            </Button>
          </div>

          {err && (
            <div className="mt-3 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">
              {err}
            </div>
          )}

          {shouldShowShopifyProUpsell ? (
            <div className="mt-4 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
              <div className={A.title}>Upgrade to Pro for Shopify Commerce</div>
              <div className={`${A.label} mt-1`}>
                Basic users can review and copy issue details.
              </div>
              <Button
                type="button"
                variant="primary"
                onClick={() => setShowUpgrade(true)}
                minWidth="auto"
                className="mt-3 px-4"
              >
                Upgrade to Pro
              </Button>
            </div>
          ) : null}

          <div className="mt-4 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
            <div className="text-[13px] font-semibold text-gray-700">
              {loading
                ? "Loading sync issues…"
                : activeIssues.length === 0
                  ? "No issues require attention"
                  : `${activeIssues.length} ${
                      activeIssues.length === 1
                        ? "issue requires"
                        : "issues require"
                    } attention`}
            </div>
            <div className={`${A.label} mt-1`}>
              {loading
                ? "Checking Shopify sync status."
                : `${resolvedIssues} resolved ${
                    resolvedIssues === 1 ? "issue" : "issues"
                  } in history.`}
            </div>
          </div>

          {query ? (
            <div className="mt-3 text-xs text-gray-500">
              Showing {filtered.length} search result
              {filtered.length === 1 ? "" : "s"}.
            </div>
          ) : null}

          <div className="mt-4 space-y-3">
            {filtered.map((issue) => {
              const type = issueType(issue);
              const mappingLink = isMappingIssue(issue)
                ? companyRoute(company_id, "/commerce/shopify/product-mapping")
                : null;
              const code = normalizeCode(issue);
              const objectType = issue.shopify_object_type || "object";
              const objectId = issue.shopify_object_id || "—";
              const contextLine = issueContextLine(issue);
              const orderLink =
                objectType.toLowerCase().includes("order") &&
                issue.shopify_object_id
                  ? shopifyAdminOrderUrl(shopDomain, issue.shopify_object_id)
                  : null;
              const webhookLink =
                objectType.toLowerCase().includes("webhook") &&
                issue.shopify_object_id
                  ? companyRoute(
                      company_id,
                      `/commerce/shopify/diagnostics?q=${encodeURIComponent(issue.shopify_object_id)}`,
                    )
                  : null;

              return (
                <div
                  key={issue.id}
                  className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={pillClass(type.tone)}>
                          {type.label}
                        </span>
                      </div>

                      <div className="mt-2 text-[13px] font-semibold text-gray-700">
                        {issueTitle(issue)}
                      </div>

                      <div className="mt-1 text-[13px] text-gray-600">
                        {contextLine}
                      </div>

                      <div className="mt-2 text-xs text-gray-500">
                        {titleize(objectType)} · {objectId} ·{" "}
                        {shortDate(issue.created_at)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 rounded-xl border border-appleblue/10 bg-white px-3 py-2">
                    <div className={A.label}>Suggested action</div>
                    <div className="mt-1 text-[13px] text-gray-700">
                      {suggestedAction(issue)}
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {mappingLink ? (
                      <Link
                        to={mappingLink}
                        className="inline-flex h-10 items-center justify-center rounded-xl border border-gray-200 bg-white px-4 text-[13px] font-medium leading-none text-appleblue shadow-sm hover:bg-gray-50"
                      >
                        Open Product Mapping
                      </Link>
                    ) : null}

                    {orderLink ? (
                      <a
                        href={orderLink}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex h-10 items-center justify-center rounded-xl border border-gray-200 bg-white px-4 text-[13px] font-medium leading-none text-appleblue shadow-sm hover:bg-gray-50"
                      >
                        Open in Shopify
                      </a>
                    ) : null}

                    {webhookLink ? (
                      <Link
                        to={webhookLink}
                        className="inline-flex h-10 items-center justify-center rounded-xl border border-gray-200 bg-white px-4 text-[13px] font-medium leading-none text-appleblue shadow-sm hover:bg-gray-50"
                      >
                        View related webhook
                      </Link>
                    ) : null}

                    {(issue.status || "").toLowerCase() !== "resolved" ? (
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => markReviewed(issue)}
                        disabled={resolvingId === issue.id}
                        minWidth="auto"
                        className="px-3"
                      >
                        {resolvingId === issue.id
                          ? "Marking…"
                          : "Mark reviewed"}
                      </Button>
                    ) : null}

                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => copyIssue(issue)}
                      minWidth="auto"
                      className="px-3"
                    >
                      {copiedId === issue.id ? "Copied" : "Copy details"}
                    </Button>

                    {shouldShowShopifyProUpsell ? (
                      <button
                        type="button"
                        onClick={() => setShowUpgrade(true)}
                        className="inline-flex items-center justify-center rounded-xl border border-appleblue/20 bg-white px-3 py-2 text-xs font-medium leading-none text-appleblue hover:bg-appleblue/5"
                      >
                        Upgrade to Pro
                      </button>
                    ) : null}
                  </div>

                  <details className="mt-3 rounded-xl border border-gray-100 bg-white px-3 py-2">
                    <summary className="cursor-pointer text-xs font-medium text-gray-500">
                      Technical details
                    </summary>
                    <div className="mt-2 space-y-2 text-xs text-gray-500">
                      <div>
                        <span className="font-medium text-gray-600">
                          Issue ID:
                        </span>{" "}
                        {issue.id}
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">
                          Issue code:
                        </span>{" "}
                        {code || "—"}
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">
                          Original message:
                        </span>{" "}
                        {issue.message || "—"}
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">
                          Resolved:
                        </span>{" "}
                        {shortDate(issue.resolved_at)}
                      </div>
                    </div>
                  </details>
                </div>
              );
            })}

            {!loading && filtered.length === 0 ? (
              <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-6 text-center text-[13px] text-gray-500">
                No Shopify sync issues found.
              </div>
            ) : null}
          </div>
        </div>
        )}
      </main>
      {!shopifyBillingRequired ? (
        <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} />
      ) : null}
    </SectionShell>
  );
}
